import React, { Suspense } from 'react'
import ShimmerEffect from '@/components/ui/shimmer'

const CalendarPicker = React.lazy(() => import('./CalendarPicker'))

// Calendar.scss sizes the grid in square 40px cells, so a month is seven of them across. The
// placeholder reserves the tallest case: a 40px navigation row + 16px below it + a 32px
// weekday row + the six day rows a month can need.
const DAY_SIZE = 40
const NAV_HEIGHT = DAY_SIZE + 16
const WEEKDAY_HEIGHT = 32
const PICKER_WIDTH = DAY_SIZE * 7
const PICKER_HEIGHT = NAV_HEIGHT + WEEKDAY_HEIGHT + DAY_SIZE * 6

const WEEKDAYS = Array.from({ length: 7 })
const WEEKS = Array.from({ length: 6 })

const CalendarSkeleton = () => (
  <ShimmerEffect className="w-full">
    <div className="flex h-[56px] items-center">
      <div className="h-3 w-24 rounded bg-surface-3" />
    </div>
    <div className="grid h-[32px] grid-cols-7 items-center">
      {WEEKDAYS.map((_, i) => (
        <div key={i} className="flex justify-center">
          <div className="h-2 w-4 rounded bg-surface-3/60" />
        </div>
      ))}
    </div>
    {WEEKS.map((_, week) => (
      <div key={week} className="grid h-[40px] grid-cols-7 items-center">
        {WEEKDAYS.map((_, day) => (
          <div key={day} className="flex justify-center">
            <div className="h-6 w-6 rounded-full bg-surface-3/50" />
          </div>
        ))}
      </div>
    ))}
  </ShimmerEffect>
)

/**
 * Calendar component supporting both single and range selection modes. The picker itself is
 * code-split; the loading state holds its footprint so the modal keeps its height while the
 * chunk loads.
 * Props:
 * - mode: 'single' | 'range' (default: 'single')
 * - value: selected date or range
 * - onChange: callback with selected date or range
 * - minDate: minimum selectable date
 * - maxDate: maximum selectable date
 * - fullWidth: stretch the grid across its container (mobile bottom sheet)
 */
const Calendar = ({ fullWidth = false, ...props }) => (
  <div
    className={fullWidth ? 'Calendar Calendar--full-width' : 'Calendar'}
    style={fullWidth ? undefined : { maxWidth: PICKER_WIDTH }}>
    {/* The reserved height belongs to the placeholder alone. On the wrapper it would also
        apply once the picker is in, leaving a blank row under every month that draws in
        five — most of them. */}
    <Suspense
      fallback={
        <div style={{ minHeight: PICKER_HEIGHT }}>
          <CalendarSkeleton />
        </div>
      }>
      <CalendarPicker {...props} />
    </Suspense>
  </div>
)

export default Calendar
